/**
 * Ticket TR1: Ereignisse an den dataLayer des Tag Managers.
 *
 * Diese Ereignisse werden bewusst UNABHÄNGIG von der Einwilligung gesendet:
 * Der dataLayer ist nur eine Liste im Browser. Ob daraus eine Messung entsteht,
 * entscheidet der Tag Manager über den Consent Mode – und er lädt ohnehin nur
 * nach Zustimmung. Einwilligungsgebundene Ereignisse bleiben in
 * `analytics-events.ts`.
 */
import { useEffect, useRef } from "react";

export type DataLayerEvent = { event: string } & Record<string, unknown>;

export function pushDataLayerEvent(payload: DataLayerEvent): void {
  if (typeof window === "undefined") return;
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ...payload });
  } catch {
    /* Messung darf die Seite nie blockieren */
  }
}

/**
 * Sendet das Ereignis genau einmal je Seitenaufruf – beim vollständigen Laden
 * wie bei Navigation in der Anwendung. Erneutes Rendern (auch der doppelte
 * Effektlauf im Entwicklungsmodus) sendet nicht erneut, weil der Merker am
 * Seitenbaustein hängt. Ein neuer Aufruf der Seite baut sie neu auf.
 */
export function useOnceDataLayerEvent(payload: DataLayerEvent): void {
  const sentRef = useRef<string | null>(null);
  const key = JSON.stringify(payload);
  useEffect(() => {
    if (sentRef.current === key) return;
    sentRef.current = key;
    pushDataLayerEvent(JSON.parse(key) as DataLayerEvent);
  }, [key]);
}

/**
 * E-Commerce-Ereignis nach GA4-Muster: vorher das alte ecommerce-Objekt im
 * dataLayer leeren, damit keine Werte aus früheren Ereignissen mitlaufen.
 */
export function pushEcommerceEvent(payload: DataLayerEvent): void {
  if (typeof window === "undefined") return;
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
  } catch {
    return;
  }
  pushDataLayerEvent(payload);
}

/**
 * Merker im Browser, ob ein Ereignis zu einer Kennung schon gesendet wurde
 * (übersteht Neuladen). Ohne Speicherzugriff gilt der Merker nur für diesen Besuch.
 */
const sentThisVisit = new Set<string>();

export function wasSentBefore(key: string): boolean {
  if (sentThisVisit.has(key)) return true;
  try {
    return window.localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

export function markSent(key: string): void {
  sentThisVisit.add(key);
  try {
    window.localStorage.setItem(key, "1");
  } catch {
    /* ohne Speicher gilt der Merker nur für diesen Besuch */
  }
}

/**
 * Vorgemerktes Ereignis für die nächste Seite: Das Formular merkt vor, die
 * Danke-Seite sendet und löscht den Merker. So zählt nur ein tatsächliches
 * Absenden, nicht ein direkter Aufruf oder ein Neuladen der Danke-Seite.
 */
const pendingThisVisit = new Set<string>();

export function flagPendingEvent(name: string): void {
  pendingThisVisit.add(name);
  try {
    window.sessionStorage.setItem(`creategy_pending:${name}`, "1");
  } catch {
    /* ohne Speicher gilt der Merker nur innerhalb der Anwendung */
  }
}

export function takePendingEvent(name: string): boolean {
  let pending = pendingThisVisit.delete(name);
  try {
    const key = `creategy_pending:${name}`;
    if (window.sessionStorage.getItem(key) === "1") pending = true;
    window.sessionStorage.removeItem(key);
  } catch {
    /* siehe flagPendingEvent */
  }
  return pending;
}

/** Ticket TR1: Der Tag Manager lädt ausschließlich auf der Live-Domain. */
export const GTM_ALLOWED_HOSTS = ["creategy.de", "www.creategy.de"] as const;

export function isGtmAllowedHost(hostname: string): boolean {
  return (GTM_ALLOWED_HOSTS as readonly string[]).includes(hostname.toLowerCase());
}
